import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { setBrokenImageFallback } from "../../../utils/imageFallback";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import cx from "classnames";
import { useLayout } from "../../../theme/LayoutContext";
import { NavbarTypes } from "../../../theme/layoutConstants";
import { getNavbarThemeVars } from "../../utils/theme";
import Profile from "../../../assets/images/couple-default.png";
import corruptedImg from "../../../assets/images/corrupted-img.png";
import Menu from "../../images/sidebar/basil/Menu";
import Exchange from "../../images/sidebar/basil/Exchange";
import Cross from "../../images/sidebar/basil/Cross";
import Settings from "../../images/sidebar/basil/Settings";
import GlobalSearch from "./GlobalSearch";
import s from "./Header.module.scss";

interface HeaderProps {
  logoutUser?: (() => void) | null;
}

function IconLock(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2zm-7-2a2 2 0 1 1 4 0v2h-4V6zm7 13H7v-9h10v9zm-5-6a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z" />
    </svg>
  );
}

function IconShield(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2 4 5v6c0 5.25 3.4 10.15 8 11.4 4.6-1.25 8-6.15 8-11.4V5l-8-3zm0 2.18 6 2.25v4.67c0 4.14-2.63 8.08-6 9.23-3.37-1.15-6-5.09-6-9.23V6.43l6-2.25z" />
    </svg>
  );
}

function IconDashboard(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 3h8v8H3V3zm10 0h8v5h-8V3zM3 13h5v8H3v-8zm7 0h11v8H10v-8z" />
    </svg>
  );
}

function IconHelp(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 .001 20.001A10 10 0 0 0 12 2zm0 16.2a1.2 1.2 0 1 1 0-2.4 1.2 1.2 0 0 1 0 2.4zm1.3-5.05-.55.35a1 1 0 0 0-.45.84V15h-1.5v-.7a2.5 2.5 0 0 1 1.15-2.12l.75-.48A1.5 1.5 0 0 0 13 10.5a1.5 1.5 0 0 0-3 0H8.5a3 3 0 1 1 4.8 2.65z" />
    </svg>
  );
}

function IconUser(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 12a4.5 4.5 0 1 0-4.5-4.5A4.5 4.5 0 0 0 12 12zm0 2c-4 0-7.5 2-7.5 4.5V20h15v-1.5C19.5 16 16 14 12 14z" />
    </svg>
  );
}

function IconPower(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13 3h-2v10h2V3zm4.83 2.17-1.42 1.42A6.97 6.97 0 0 1 19 12a7 7 0 1 1-14 0c0-2.05.89-3.9 2.3-5.17L5.9 5.41A8.96 8.96 0 0 0 3 12a9 9 0 1 0 18 0c0-2.6-1.1-4.95-2.87-6.58z" />
    </svg>
  );
}

function IconChevron(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
    </svg>
  );
}

function Header({ logoutUser = null }: HeaderProps) {
  const location = useLocation();
  const {
    sidebarOpened,
    sidebarStatic,
    navbarType,
    navbarColor,
    openSidebar,
    closeSidebar,
    toggleSidebarStatic,
    changeActiveSidebarItem,
  } = useLayout();

  const userData: Record<string, any> | null = useAuthUser();

  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const switchSidebar = () => {
    if (sidebarOpened) {
      closeSidebar();
      changeActiveSidebarItem(null);
    } else {
      const paths = location.pathname.split("/");
      paths.pop();
      openSidebar();
      changeActiveSidebarItem(paths.join("/"));
    }
  };

  const handleToggleSidebar = () => {
    toggleSidebarStatic();
    if (sidebarStatic) {
      changeActiveSidebarItem(null);
    } else {
      const paths = location.pathname.split("/");
      paths.pop();
      changeActiveSidebarItem(paths.join("/"));
    }
  };

  const firstName = userData?.firstName || userData?.hisFirstName || "";
  const lastName = userData?.lastName || "";
  const displayName = `${firstName} ${lastName}`.trim() || "User";
  const email = userData?.email || userData?.hisEmail || "";
  const firstLetter = (firstName || email || "U").charAt(0).toUpperCase();
  const avatarSrc = userData?.imageUpload || null;
  const isFullAdmin =
    userData?.userRole === "ADMIN" && userData?.isDisabled === 0;

  const closeMenu = () => setMenuOpen(false);

  return (
    <nav
      className={cx(
        s.root,
        "sing-header",
        "d-print-none",
        navbarType === NavbarTypes.FLOATING && s.navbarFloatingType
      )}
      style={{
        zIndex: 100,
        backgroundColor: getNavbarThemeVars(navbarColor)["--navbar-bg"],
        ...getNavbarThemeVars(navbarColor),
      }}
    >
      <div className="d-flex flex-row flex-grow-1 align-items-center">
        <ul className="sing-header-nav my-auto">
          <li>
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="toggleSidebarTip">
                  Turn on/off
                  <br />
                  sidebar
                  <br />
                  collapsing
                </Tooltip>
              }
            >
              <button
                type="button"
                className={cx(
                  "sing-header-link",
                  "d-md-down-none",
                  s.toggleSidebar
                )}
                id="toggleSidebar"
                onClick={handleToggleSidebar}
              >
                <span className={s.headerSvgFlipColor}>
                  <Menu />
                </span>
              </button>
            </OverlayTrigger>
            <button
              type="button"
              className="sing-header-link fs-lg d-md-none"
              onClick={switchSidebar}
            >
              <span className={s.headerSvgFlipColor}>
                <Menu />
              </span>
            </button>
          </li>
          {/* <li className="d-sm-down-none">
            <span className={cx("sing-header-link", "px-2")}>
              <span className={s.headerSvgFlipColor}>
                <Exchange />
              </span>
            </span>
          </li> */}
          {/* <li className="d-sm-down-none">
            <span className={cx("sing-header-link", "px-2")}>
              <span className={s.headerSvgFlipColor}>
                <Cross />
              </span>
            </span>
          </li> */}
        </ul>

        <form
          className={s.headerSearchInput}
          onSubmit={(e) => e.preventDefault()}
        >
          <GlobalSearch />
        </form>

        <div className={cx(s.navbarBrand, "d-md-none", s.headerSvgFlipColor)}>
          <i className="fa fa-circle text-primary" style={{ marginRight: -4 }} />
          <i className="fa fa-circle text-danger" />
          &nbsp;LoveStrong&nbsp;
          <i className="fa fa-circle text-danger" style={{ marginRight: -4 }} />
          <i className="fa fa-circle text-primary" />
        </div>
      </div>

      <div>
        <ul className={cx("sing-header-nav", "ms-auto", s.rightActions)}>
          <Dropdown
            show={menuOpen}
            onToggle={(next) => setMenuOpen(next)}
            align="end"
            className={s.accountMenu}
            as="li"
          >
            <Dropdown.Toggle
              as="button"
              type="button"
              className={cx("sing-header-link", s.accountToggle)}
              id="account-menu-toggle"
            >
              <span className={cx(s.avatar, "rounded-circle")}>
                {avatarSrc ? (
                  <img
                    src={avatarSrc}
                    alt=""
                    title={displayName}
                    onError={(e) => setBrokenImageFallback(e, corruptedImg)}
                  />
                ) : (
                  <span title={displayName}>{firstLetter}</span>
                )}
              </span>
              <span
                className={cx(
                  "d-sm-down-none",
                  s.headerTitle,
                  s.accountName,
                  sidebarStatic && s.adminEmail
                )}
              >
                {displayName}
              </span>
              <span className={cx(s.notifBadge, "d-sm-down-none")}>1</span>
              <span className={cx(s.headerSvgFlipColor, s.chevron, "d-sm-down-none")}>
                <IconChevron />
              </span>
            </Dropdown.Toggle>

            <Dropdown.Menu className={s.accountDropdown}>
              <div className={s.accountHeader}>
                <div className={s.accountAvatarWrap}>
                  <img
                    className={s.accountAvatar}
                    src={avatarSrc || Profile}
                    alt=""
                    onError={(e) => setBrokenImageFallback(e, Profile)}
                  />
                </div>
                <div className={s.accountMeta}>
                  <div className={s.accountMetaName}>{displayName}</div>
                  <div className={s.accountMetaEmail}>{email}</div>
                  {userData?.userRole ? (
                    <span className={s.rolePill}>{userData.userRole}</span>
                  ) : null}
                </div>
              </div>

              <div className={s.accountBody}>
                <Dropdown.Item
                  as={Link}
                  to="/my-profile"
                  className={s.accountItem}
                  onClick={closeMenu}
                >
                  <span className={cx(s.itemIcon, s.iconBlue)}>
                    <IconLock />
                  </span>
                  <span className={s.itemText}>
                    <strong>Change Password</strong>
                    <small>Update your login credentials</small>
                  </span>
                </Dropdown.Item>

                <Dropdown.Item
                  as={Link}
                  to="/update-couple"
                  className={s.accountItem}
                  onClick={closeMenu}
                >
                  <span className={cx(s.itemIcon, s.iconTeal)}>
                    <IconUser />
                  </span>
                  <span className={s.itemText}>
                    <strong>Update Profile</strong>
                    <small>Edit your couple profile</small>
                  </span>
                </Dropdown.Item>

                <Dropdown.Item
                  as={Link}
                  to="/create-ticket"
                  target="_blank"
                  className={s.accountItem}
                  onClick={closeMenu}
                >
                  <span className={cx(s.itemIcon, s.iconAmber)}>
                    <IconHelp />
                  </span>
                  <span className={s.itemText}>
                    <strong>Help?</strong>
                    <small>Create a support ticket</small>
                  </span>
                </Dropdown.Item>

                {isFullAdmin ? (
                  <>
                    <div className={s.menuDivider} />
                    <Dropdown.Item
                      as={Link}
                      to="/Access-SuperAdmin"
                      className={s.accountItem}
                      onClick={closeMenu}
                    >
                      <span className={cx(s.itemIcon, s.iconPurple)}>
                        <IconShield />
                      </span>
                      <span className={s.itemText}>
                        <strong>Access SuperAdmin</strong>
                        <small>Super admin tools</small>
                      </span>
                    </Dropdown.Item>
                    <Dropdown.Item
                      as={Link}
                      to="/admin-dashboard"
                      className={s.accountItem}
                      onClick={closeMenu}
                    >
                      <span className={cx(s.itemIcon, s.iconIndigo)}>
                        <IconDashboard />
                      </span>
                      <span className={s.itemText}>
                        <strong>Admin Dashboard</strong>
                        <small>Users &amp; roles</small>
                      </span>
                    </Dropdown.Item>
                  </>
                ) : null}

                <div className={s.menuDivider} />
                <Dropdown.Item
                  className={cx(s.accountItem, s.signOutItem)}
                  onClick={() => {
                    closeMenu();
                    if (logoutUser) logoutUser();
                  }}
                >
                  <span className={cx(s.itemIcon, s.iconRed)}>
                    <IconPower />
                  </span>
                  <span className={s.itemText}>
                    <strong>Sign Out</strong>
                    <small>Log out of your account</small>
                  </span>
                </Dropdown.Item>
              </div>
            </Dropdown.Menu>
          </Dropdown>

          <li>
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="settingsTip">Account settings</Tooltip>}
            >
              <button
                type="button"
                className={cx("sing-header-link", s.settingsBtn, s.mobileCog)}
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="Open account menu"
              >
                <span className={s.headerSvgFlipColor}>
                  <Settings />
                </span>
              </button>
            </OverlayTrigger>
          </li>
        </ul>
      </div>
    </nav>
  );
}

export default Header;
